import React, { useState, useEffect } from 'react';
import { toast } from 'react-semantic-toasts';
import _ from 'lodash';
import { useMutation } from '@apollo/client';
import {
  usePaginatedQuery, ErrorHandler, InfiniteScrollComponent, captureException,
} from '@playbookux/playbook-web-core';

import SearchField from '../../../core/layout-components/search';
import Table from './custom-table';
import UsersModal from './users-modal';
import PrepaidBalanceModal from './prepaid-balance-modal';
import ModeratedSettingsModal from './moderated-settings-modal';
import PasswordLinkModal from './password-link-modal';
import useDebounce from '../../../core/hooks/use-debounce';
import withDashboardSidebar from '../../components/with-dashboard-sidebar';

import {
  Container,
  ContainerSearch,
  ContainerTop,
  Title,
} from './styles';

import {
  CHANGE_MODERATED_SETTINGS,
  CHANGE_BALANCE,
  CHANGE_CUSTOMER_TYPE,
  CHANGE_PLAN,
  CHANGE_TOTAL_AMOUNT,
  DEBOUNCE_TIME,
  GET_ALL_COMPANIES,
  LIMIT,
  ORDER,
  GENERATE_PASSWORD_LINK,
  UPDATE_START_CONTRACT_DATE,
  UPDATE_RENEWAL_DATE,
} from './graphql';
import TotalAmountModal from './total-amount-modal';

const Companies = () => {
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebounce(() => search, DEBOUNCE_TIME, [search]);
  const [showUsersModal, setShowUsersModal] = useState(false);
  const [companyUsers, setCompanyUsers] = useState([]);
  const [showPrepaidBalanceModal, setShowPrepaidBalanceModal] = useState(false);
  const [showModeratedSettingsModal, setShowModeratedSettingsModal] = useState(false);
  const [showPasswordLinkModal, setShowPasswordLinkModal] = useState(false);
  const [showTotalAmountModal, setShowTotalAmountModal] = useState(false);
  const [resetPasswordToken, setResetPasswordToken] = useState('');
  const [loadingPasswordToken, setLoadingPasswordToken] = useState(false);
  const [companySelected, setCompanySelected] = useState({});
  const [prepaidBalanceError, setPrepaidBalanceError] = useState();
  const [moderatedSettingsError, setModeratedSettingsError] = useState();
  const [totalAmountError, setTotalAmountError] = useState();

  const attributes = {
    variables: {
      limit: LIMIT,
      offset: 0,
      order: ORDER,
      search: debouncedSearch,
    },
  };

  const {
    loading,
    error,
    data = {},
    fetchMore,
    hasMore,
  } = usePaginatedQuery(GET_ALL_COMPANIES, attributes, { dataField: 'companies' });

  const TOAST_TIME = 2000;

  const [changePlan] = useMutation(CHANGE_PLAN);
  const [changeCustomerType] = useMutation(CHANGE_CUSTOMER_TYPE);
  const [updatePrepaidBalance, { loading: updatingBalance }] = useMutation(CHANGE_BALANCE);
  const [updateTotalAmount, { loading: updatingTotalAmount }] = useMutation(CHANGE_TOTAL_AMOUNT);
  const [updateStartContractDate] = useMutation(UPDATE_START_CONTRACT_DATE);
  const [updateRenewalDate] = useMutation(UPDATE_RENEWAL_DATE);
  const [updateModeratedSettings, { loading: updatingModeratedSettings }] = useMutation(CHANGE_MODERATED_SETTINGS);
  const [generatePasswordLink] = useMutation(GENERATE_PASSWORD_LINK);

  const { companies = [] } = data;

  const handleChangePlan = async (companyId, plan) => {
    try {
      await changePlan({
        variables: {
          companyId,
          plan,
        },
      });
    } catch (err) {
      captureException('CHANGE_PLAN_FAILURE', err);
    }
  };

  const handleChangeCustomerType = async (companyId, customerType) => {
    try {
      await changeCustomerType({
        variables: {
          companyId,
          customerType,
        },
      });
    } catch (err) {
      captureException('CHANGE_CUSTOMER_TYPE_FAILURE', err);
    }
  };

  const handleSubmitBalance = async (companyId, value) => {
    try {
      const amountRemaining = parseInt(value, 10);

      await updatePrepaidBalance({
        variables: {
          companyId,
          amountRemaining,
        },
      });

      setPrepaidBalanceError(null);
      setShowPrepaidBalanceModal(false);
      setCompanySelected({});
    } catch (err) {
      setPrepaidBalanceError(err);
    }
  };

  const handleSubmitTotalAmount = async (companyId, value) => {
    try {
      const totalAmount = parseInt(value, 10);

      await updateTotalAmount({
        variables: {
          companyId,
          totalAmount,
        },
      });

      toast({
        type: 'success',
        description: 'Total amount updated successfully.',
        time: TOAST_TIME,
      });

      setTotalAmountError(null);
      setShowTotalAmountModal(false);
      setCompanySelected({});
    } catch (err) {
      toast({
        type: 'error',
        description: 'Error while updating total amount, please try again.',
        time: TOAST_TIME,
      });
      setTotalAmountError(err);
    }
  };

  const handleChangeStartContractDate = async (companyId, contractStartDate) => {
    try {
      await updateStartContractDate({
        variables: {
          companyId,
          contractStartDate,
        },
      });

      toast({
        type: 'success',
        description: 'Start contract date updated successfully.',
        time: TOAST_TIME,
      });
    } catch (err) {
      toast({
        type: 'error',
        description: err?.message || 'Error while updating start contract date, please try again',
        time: TOAST_TIME,
      });
    }
  };

  const handleChangeRenewalDate = async (companyId, renewalDate) => {
    try {
      await updateRenewalDate({
        variables: {
          companyId,
          renewalDate,
        },
      });

      toast({
        type: 'success',
        description: 'Renewal date updated successfully.',
        time: TOAST_TIME,
      });
    } catch (err) {
      toast({
        type: 'error',
        description: err?.message || 'Error while updating renewal date, please try again.',
        time: TOAST_TIME,
      });
    }
  };

  const handleSubmitModeratedSettings = async (companyId, callLink) => {
    try {
      const { error: updateSettingsError } = await updateModeratedSettings({
        variables: {
          companyId,
          callLink,
        },
      });

      if (updateSettingsError) {
        setModeratedSettingsError(updateSettingsError);
      } else {
        setModeratedSettingsError(null);
        setShowModeratedSettingsModal(false);
      }
    } catch (err) {
      setModeratedSettingsError(err);
    }
  };

  const handleSearchChange = (e, { value }) => {
    setSearch(value);
  };

  const handleOpenUsersModal = (companyMembers) => {
    const users = _.map(companyMembers, ({ user }) => ({
      id: user.id,
      email: user.email,
      name: `${user.profile.firstName} ${user.profile.lastName}`,
      resetPasswordToken: user.resetPasswordToken,
    }));

    setCompanyUsers(users);
    setShowUsersModal(true);
  };

  const handleOpenBalanceModal = (company) => {
    setCompanySelected(company);
    setShowPrepaidBalanceModal(true);
  };

  const handleCloseBalanceModal = () => {
    setShowPrepaidBalanceModal(false);
    setCompanySelected({});
  };

  const handleOpenModeratedSettingsModal = (company) => {
    setCompanySelected(company);
    setShowModeratedSettingsModal(true);
  };

  const handleCloseModeratedSettingsModal = () => {
    setShowModeratedSettingsModal(false);
  };

  const handleOpenPasswordLinkModal = async (id) => {
    const tempCompanyUser = _.find(companyUsers, (companyUser) => companyUser.id === id);
    setShowUsersModal(false);
    setLoadingPasswordToken(true);
    setShowPasswordLinkModal(true);
    if (tempCompanyUser.resetPasswordToken) {
      setResetPasswordToken(tempCompanyUser.resetPasswordToken);
      setLoadingPasswordToken(false);
    } else {
      try {
        const response = await generatePasswordLink({
          variables: {
            id: tempCompanyUser.id,
          },
        });
        if (response && response.data && response.data.generatePasswordRecoverLink) {
          setResetPasswordToken(response.data.generatePasswordRecoverLink.resetPasswordToken);
          setLoadingPasswordToken(false);
        }
      } catch (err) {
        captureException('GENERATE_PASSWORD_LINK_FAILURE', err);
      }
    }
  };

  const handleClosePasswordLinkModal = () => {
    setShowPasswordLinkModal(false);
    setShowUsersModal(true);
  };

  const handleOpenTotalAmountModal = (company) => {
    setCompanySelected(company);
    setShowTotalAmountModal(true);
  };

  const handleCloseTotalAmountModal = () => {
    setShowTotalAmountModal(false);
    setCompanySelected({});
  };

  useEffect(() => {
    if (!showUsersModal && !showPasswordLinkModal) {
      setCompanyUsers([]);
    }
  }, [showUsersModal, showPasswordLinkModal]);

  if (error) {
    return (
      <ErrorHandler
        error={error}
        defaultMessage="Error while retrieving companies."
      />
    );
  }

  return (
    <Container>
      <UsersModal
        onClose={() => setShowUsersModal(false)}
        onOpenPasswordLink={handleOpenPasswordLinkModal}
        show={showUsersModal}
        users={companyUsers}
      />

      <PrepaidBalanceModal
        company={companySelected}
        error={prepaidBalanceError}
        loading={updatingBalance}
        onClose={handleCloseBalanceModal}
        onSubmit={handleSubmitBalance}
        show={showPrepaidBalanceModal}
      />

      <ModeratedSettingsModal
        company={companySelected}
        error={moderatedSettingsError}
        loading={updatingModeratedSettings}
        onClose={handleCloseModeratedSettingsModal}
        onSubmit={handleSubmitModeratedSettings}
        show={showModeratedSettingsModal}
      />

      <PasswordLinkModal
        show={showPasswordLinkModal}
        onClose={handleClosePasswordLinkModal}
        resetPasswordToken={resetPasswordToken}
        loading={loadingPasswordToken}
      />

      <TotalAmountModal
        company={companySelected}
        error={totalAmountError}
        loading={updatingTotalAmount}
        onClose={handleCloseTotalAmountModal}
        onSubmit={handleSubmitTotalAmount}
        show={showTotalAmountModal}
      />

      <InfiniteScrollComponent
        hasMore={hasMore}
        onLoadMore={fetchMore}
      >
        <ContainerTop>
          <Title>Company</Title>
          <ContainerSearch>
            <SearchField
              input={{ icon: 'search', iconPosition: 'left' }}
              placeholder="Search workspace name"
              onSearchChange={handleSearchChange}
              value={search || ''}
              showNoResults={false}
              loading={loading && search}
            />
          </ContainerSearch>
        </ContainerTop>

        <Table
          companies={companies}
          onOpenUsersModal={handleOpenUsersModal}
          onChangePlan={handleChangePlan}
          onChangeCustomerType={handleChangeCustomerType}
          onChangeStartContractDate={handleChangeStartContractDate}
          onChangeRenewalDate={handleChangeRenewalDate}
          onOpenPrepaidBalanceModal={handleOpenBalanceModal}
          onOpenModeratedSettingsModal={handleOpenModeratedSettingsModal}
          onOpenTotalAmountModal={handleOpenTotalAmountModal}
        />
      </InfiniteScrollComponent>
    </Container>
  );
};

export default withDashboardSidebar(Companies);
